/**
 * Feature flags: the shared vocabulary for what only some companies have.
 *
 * A company sets each flag's built-in value in its folder (`companies/<id>/features.js`); a
 * flag a company leaves out is OFF. central-service (`companies.<id>.features` in its
 * database) can change them without a deploy:
 *
 *  - REMOTE_TOGGLES are product choices every backend supports: central-service turns them on or off.
 *  - Every other flag names backend endpoints only some companies have: central-service can turn
 *    it off (a kill switch), never on, so the admin never calls an endpoint a backend lacks.
 *
 * Shared code never asks which company is signed in, it asks `isFeatureEnabled(FEATURES.X)`
 * (`./session.js`).
 */
export const FEATURES = Object.freeze({
    // Auditor role: `/audit/*`, `/store/auditor*`, the auditor role in user forms and counts,
    // and the product ↔ audit-barcode link (`/audit/admin/audit-products`).
    AUDITOR: 'auditor',
    // Per-piso price lists: `/price/store/:storeId` (GET/POST/DELETE), `/price/store-lists/:clientId`.
    STORE_PRICE_LISTS: 'storePriceLists',
    // The price list that applies to a store: `/price/resolve/store/:storeId`.
    STORE_PRICE_RESOLVE: 'storePriceResolve',
    // Store-type exceptions per piso: `/storeType/overrides/:value`, `/storeType/override/store/:storeId`.
    STORE_OVERRIDES: 'storeOverrides',
    // Visited stores on the stores map: `/analytics/storecheck-recency`.
    STORE_CHECK_RECENCY: 'storeCheckRecency',
    // Per-store price on a contract / campaign: `/trade/{contract,campaign}/:id/store-price`.
    TRADE_STORE_PRICES: 'tradeStorePrices',
    // Deleting a poll: `DELETE /poll/:id`.
    POLL_DELETE: 'pollDelete',
    // App interactions report: `/analytics/interactions/*`.
    INTERACTIONS: 'interactions',
    // Route efficiency report: `/analytics/promoter-routes`.
    ROUTE_EFFICIENCY: 'routeEfficiency',
    // Competitor brands and categories: `/competitor-brands/*`.
    COMPETITOR_BRANDS: 'competitorBrands',
    // Segmentation types: `/segmentation-types`.
    SEGMENTATION_TYPES: 'segmentationTypes',
    // Distributor price capture ("Levantamiento" of the company's own brand): `/distributor/*`.
    DISTRIBUTOR_PRICES: 'distributorPrices',
    // General price capture: `/levantamiento/*`.
    LEVANTAMIENTO: 'levantamiento',
    // Shelf reports: `/analytics/shelf-usage`, `/analytics/share-of-shelf*`,
    // `/analytics/numeric-distribution*`, `/analytics/product-availability`.
    SHELF_REPORTS: 'shelfReports',
    // SOS analysis screen (tag targets compared over many store checks).
    SOS_ANALYSIS: 'sosAnalysis',
    // The product catalog bundled with this admin, shown until the first sync with the API.
    BUNDLED_CATALOG: 'bundledCatalog',
    // Trade-marketing polls ("Encuestas" / "Evaluación FFVV Indirecta"): `/poll`.
    POLLS: 'polls',
});

/** Flags central-service may switch on as well as off. */
export const REMOTE_TOGGLES = Object.freeze([FEATURES.POLLS]);

export const ALL_FEATURES = Object.freeze(Object.values(FEATURES));

/**
 * Screens (nav keys) that need a feature: a screen whose feature is off is closed, whatever the
 * navigation lists. A company's navigation lists such a screen only when the feature is on or
 * is a REMOTE_TOGGLE (a test enforces it).
 */
export const SCREEN_FEATURES = Object.freeze({
    polls: FEATURES.POLLS,
    auditors: FEATURES.AUDITOR,
    'auditor-field-options': FEATURES.AUDITOR,
    'audit-console': FEATURES.AUDITOR,
    'product-approval': FEATURES.AUDITOR,
    'distributor-prices': FEATURES.DISTRIBUTOR_PRICES,
    'levantamiento-general': FEATURES.LEVANTAMIENTO,
    'share-of-shelf': FEATURES.SHELF_REPORTS,
    'numeric-distribution': FEATURES.SHELF_REPORTS,
    'uso-de-shelf': FEATURES.SHELF_REPORTS,
    'route-efficiency': FEATURES.ROUTE_EFFICIENCY,
    'user-interactions': FEATURES.INTERACTIONS,
    'sos-analysis': FEATURES.SOS_ANALYSIS,
});
